import { useEffect, useMemo, useState } from "react"
import { Link, useHistory, useLocation } from "react-router-dom"
import { Anchor, Button, Divider, Group, Menu, Select, Stack, Text, Title } from "@mantine/core"
import { useLocalStorage } from "@mantine/hooks"
import { IconChevronDown, IconChevronRight, IconCircle, IconCircleCheckFilled } from "@tabler/icons-react"
import moment from "moment"

import { useProgramActivitiesQuery, useProgramActivitySessionsByProgramActivityIdsQuery, useProgramGroupsQuery } from "../../api/program.api"
import { useProgramActivitySessionByIdQuery, useProgramActivitySessionEnrollmentsByIdQuery } from "../../api/programactivitysession.api"
import { ProgramActivitySession } from "../../models/programs.models"
import ProgramActivitySessionCheckIn from "../../Components/CheckIn/ProgramActivitySessionCheckIn"

const ViewActivityCheckInPage = () => {
	const location = useLocation();
	const history = useHistory();
	const urlParams = new URLSearchParams(location.search);
	const autoRefresh = import.meta.env.MODE != 'development';
	const programActivitySessionId = urlParams.get("programActivitySessionId");
	const { data: activities, isLoading: isLoadingActivities } = useProgramActivitiesQuery();
	const { data: programGroups } = useProgramGroupsQuery();
	const [selectedProgramActivitySessionId, setSelectedProgramActivitySessionId] = useState<string | null>(programActivitySessionId);
	const { data: programActivitySession, isLoading: isLoadingProgramActivitySession } = useProgramActivitySessionByIdQuery(Number(selectedProgramActivitySessionId ?? 0));
	const { data: programActivitySessionEnrollments, isLoading: isLoadingProgramActivitySessionEnrollments } = useProgramActivitySessionEnrollmentsByIdQuery(programActivitySession?.Id ?? 0, autoRefresh);

	const [selectedProgramGroup, setSelectedProgramGroup] = useLocalStorage({ key: 'calendar-view-program-group', defaultValue: 0 });
	const [selectedProgramActivityId, setSelectedProgramActivityId] = useState<string | null>(urlParams.get("programActivityId"));

	const formatProgramActivitySessionLabel = (session: ProgramActivitySession) => {
		if (moment(session.StartDate).isSame(moment(session.EndDate), "date")) {
			return `${moment(session.StartDate).format("LL")}, ${moment(session.StartDate).format("h:mma")} - ${moment(session.EndDate).format("h:mma")}`;
		}

		return `${moment(session.StartDate).format("LL")}, ${moment(session.StartDate).format("h:mma")} - ${moment(session.EndDate).format("LL")}, ${moment(session.EndDate).format("h:mma")}`;
	}

	const getProgramActivitySessionDistance = (session: ProgramActivitySession) => {
		const today = moment();
		const start = moment(session.StartDate);
		const end = moment(session.EndDate);

		if (today.isBetween(start, end, undefined, "[]")) {
			return 0;
		}

		if (today.isBefore(start)) {
			return start.diff(today);
		}

		return today.diff(end);
	}

	const isProgramActivitySessionInCurrentYear = (session: ProgramActivitySession) => {
		const currentYearStart = moment().startOf("year");
		const currentYearEnd = moment().endOf("year");
		const start = moment(session.StartDate);
		const end = moment(session.EndDate);

		return start.isSameOrBefore(currentYearEnd) && end.isSameOrAfter(currentYearStart);
	}

	const updateQueryParam = (key: string, value: string | null) => {
		const params = new URLSearchParams(location.search);
		if (value) {
			params.set(key, value);
		} else {
			params.delete(key);
		}

		history.push({ pathname: location.pathname, search: params.toString() });
	}

	const handleProgramActivityChange = (value: string | null) => {
		setSelectedProgramActivityId(value);
		setSelectedProgramActivitySessionId(null);

		const params = new URLSearchParams(location.search);
		if (value) {
			params.set("programActivityId", value);
		} else {
			params.delete("programActivityId");
		}
		params.delete("programActivitySessionId");

		history.push({ pathname: location.pathname, search: params.toString() });
	}

	const handleProgramGroupChange = (value: number) => {
		setSelectedProgramGroup(value);

		const selectedProgramActivity = activities?.find((activity) => activity.Id === Number(selectedProgramActivityId));
		if (!selectedProgramActivity) {
			return;
		}

		if (value > 0 && selectedProgramActivity.ProgramGroupId !== value) {
			handleProgramActivityChange(null);
		}
	}

	const handleProgramActivitySessionChange = (value: string | null) => {
		setSelectedProgramActivitySessionId(value);
		updateQueryParam("programActivitySessionId", value);
	}
	
	const programActivitySelectData = useMemo(() => activities?.map((activity) => ({ label: activity.Title, value: activity.Id.toString() })) ?? [], [activities]);
	const programActivityIds = activities?.filter((activity) => selectedProgramActivityId ? activity.Id === Number(selectedProgramActivityId) : true).map((activity) => activity.Id) ?? [];
	const { data: programActivitySessions, isLoading: isLoadingProgramActivitySessions } = useProgramActivitySessionsByProgramActivityIdsQuery(programActivityIds);

	const selectedProgramActivitySessions = useMemo(() => {
		return programActivitySessions?.filter((session) => selectedProgramActivityId ? session.ActivityId === Number(selectedProgramActivityId) && isProgramActivitySessionInCurrentYear(session) : false) ?? [];
	}, [selectedProgramActivityId, programActivitySessions]);

	const orderedProgramActivitySessions = useMemo(() => {
		const currentAndFutureSessions = selectedProgramActivitySessions
			.filter((session) => moment(session.EndDate).isSameOrAfter(moment(), "day"))
			.sort((left, right) => getProgramActivitySessionDistance(left) - getProgramActivitySessionDistance(right) || moment(left.StartDate).diff(moment(right.StartDate)));

		const pastSessions = selectedProgramActivitySessions
			.filter((session) => moment(session.EndDate).isBefore(moment(), "day"))
			.sort((left, right) => moment(right.EndDate).diff(moment(left.EndDate)));

		return {
			currentAndFutureSessions,
			pastSessions,
		};
	}, [selectedProgramActivitySessions]);

	const programActivitySessionSelectData = useMemo(() => {
		const selectData: { group: string, items: { label: string, value: string }[] }[] = [];

		if (orderedProgramActivitySessions.currentAndFutureSessions.length > 0) {
			selectData.push({
				group: "Current & Upcoming Sessions",
				items: orderedProgramActivitySessions.currentAndFutureSessions.map((session) => ({
					label: formatProgramActivitySessionLabel(session),
					value: session.Id.toString(),
				}))
			});
		}

		if (orderedProgramActivitySessions.pastSessions.length > 0) {
			selectData.push({
				group: "Past Sessions",
				items: orderedProgramActivitySessions.pastSessions.map((session) => ({
					label: formatProgramActivitySessionLabel(session),
					value: session.Id.toString(),
				}))
			});
		}

		return selectData;
	}, [orderedProgramActivitySessions]);
	const checkedInCount = useMemo(() => {
		return programActivitySessionEnrollments?.filter((enrollment) => enrollment.ProgramActivitySessionEnrollment.CheckedIn === true).length ?? 0;
	}, [programActivitySessionEnrollments]);
	const totalParticipants = programActivitySessionEnrollments?.length ?? 0;
	const checkInSummary = selectedProgramActivitySessionId ? `${checkedInCount}/${totalParticipants}` : "0/0";

	const isLoading = isLoadingActivities || isLoadingProgramActivitySessions;

	useEffect(() => {
		const nextProgramActivitySessionId = new URLSearchParams(location.search).get("programActivitySessionId");
		if ((nextProgramActivitySessionId ?? null) !== selectedProgramActivitySessionId) {
			setSelectedProgramActivitySessionId(nextProgramActivitySessionId);
		}
	}, [location.search, selectedProgramActivitySessionId]);

	useEffect(() => {
		if (selectedProgramActivityId == null || activities == null) {
			return;
		}

		const selectedProgramActivity = activities.find((activity) => activity.Id === Number(selectedProgramActivityId));
		if (!selectedProgramActivity) {
			handleProgramActivityChange(null);
			return;
		}

		if (selectedProgramGroup !== selectedProgramActivity.ProgramGroupId) {
			setSelectedProgramGroup(selectedProgramActivity.ProgramGroupId);
		}
	}, [activities, selectedProgramGroup, selectedProgramActivityId, setSelectedProgramGroup]);

	useEffect(() => {
		if (isLoadingActivities || isLoadingProgramActivitySessions) {
			return;
		}

		if (selectedProgramActivityId == null) {
			if (selectedProgramActivitySessionId !== null) {
				handleProgramActivitySessionChange(null);
			}
			return;
		}

		if (selectedProgramActivitySessions.length === 0) {
			if (selectedProgramActivitySessionId !== null) {
				handleProgramActivitySessionChange(null);
			}
			return;
		}

		const hasSelectedSession = selectedProgramActivitySessions.some((session) => session.Id === Number(selectedProgramActivitySessionId));
		if (hasSelectedSession) {
			return;
		}

		const defaultSession = orderedProgramActivitySessions.currentAndFutureSessions[0] ?? orderedProgramActivitySessions.pastSessions[0] ?? null;
		if (defaultSession) {
			handleProgramActivitySessionChange(defaultSession.Id.toString());
		}
	}, [isLoadingActivities, isLoadingProgramActivitySessions, orderedProgramActivitySessions, selectedProgramActivityId, selectedProgramActivitySessionId, selectedProgramActivitySessions]);

	return <>
		<Group justify="space-between" align="start">
			<Stack gap={0}>
				<Group gap={5}>
					<Anchor component={Link} to="/" fz="sm" fw="bold">Home</Anchor>
					<IconChevronRight size="14" />
				</Group>
				<Title order={1}>Skills Check In &amp; Out</Title>
			</Stack>
			<Group align="start">
				
				<Menu shadow="md" position="bottom-end">
					<Menu.Target>
						<Button variant="filled">
							{selectedProgramGroup === 0 ? "All Programs" : programGroups?.find(p => p.Id === selectedProgramGroup)?.Title} <IconChevronDown />
						</Button>
					</Menu.Target>
					<Menu.Dropdown>
						<Menu.Item leftSection={selectedProgramGroup === 0 ? <IconCircleCheckFilled /> : <IconCircle />} onClick={() => setTimeout(() => handleProgramGroupChange(0), 100)}><Text inherit fw={selectedProgramGroup === 0 ? "bold":"normal"}>All Programs</Text></Menu.Item>
						{programGroups?.map((programGroup, index) => {
							return <Menu.Item key={index} leftSection={selectedProgramGroup === programGroup.Id ? <IconCircleCheckFilled /> : <IconCircle />} onClick={() => setTimeout(() => handleProgramGroupChange(programGroup.Id), 100)}><Text inherit fw={selectedProgramGroup === programGroup.Id ? "bold":"normal"}>{programGroup.Title}</Text></Menu.Item>
						})}
					</Menu.Dropdown>
				</Menu>
			</Group>
		</Group>
	
		<Group mt="md" align="start" justify="space-between" wrap="nowrap">
			<Group>
				<Select
					data={programActivitySelectData}
					value={selectedProgramActivityId}
					onChange={handleProgramActivityChange}
					placeholder="Select program activity..."
					clearable
					w={260}
				/>
				<Select
					data={programActivitySessionSelectData}
					value={selectedProgramActivitySessionId}
					onChange={handleProgramActivitySessionChange}
					placeholder="Select session..."
					disabled={!selectedProgramActivityId || selectedProgramActivitySessions.length === 0}
					clearable
					w={380}
					nothingFoundMessage={selectedProgramActivityId ? "No sessions found" : "Select a program activity first"}
				/>
			</Group>
			<Text fz="h1" fw="bold" mt={-10}>{checkInSummary}</Text>
		</Group>

		<Divider my="md" />
				
		<ProgramActivitySessionCheckIn
			enrollments={programActivitySessionEnrollments ?? []}
			isLoading={isLoading || isLoadingProgramActivitySessions || isLoadingProgramActivitySessionEnrollments}
			programActivitySessionId={Number(selectedProgramActivitySessionId ?? 0)}
		/>

	</>
}

export default ViewActivityCheckInPage